import swal from "sweetalert"
import moment from "moment"

import { api } from "@/api"
import { resourceGetDetail, sendAction, resourcePatchDetail, resourceGetList } from "shared/djangio/action-creators"
import { pushPage, popPage } from "global-action-creators"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import {
    generateDotFecFileAndPdfActionDict,
    getDotFecUrlActionDict,
    getFecCheckMsgUrlActionDict,
    getPacFileActionDict,
    getLatestPacFileActionDict,
    getGeneralElectionActionDict,
    getPdfUrlActionDict,
    patchForm3XSubmissionActionDict,
    patchPacFileActionDict,
    postPacFileActionDict,
    submitForm1HistoricalActionDict,
    getJurisdictionsActionDict,
} from "shared/pac/types"
import { selectSingleLedgerSettings } from "shared/pac/selectors"
import { selectIsFilingFormSubmitting } from "pac/selectors/filings-form-selectors"
import { getPacPaths } from "imports/desktopPaths"
import * as swalConfigs from "pac/swalConfigs"
import { decipherValidationErrorMessage, isValidationError } from "app/static/frontend/forms/helperFunctions"

const { PacFile } = DjangIO.app.pac.models.pac_file
const { FecForm3XSubmission } = DjangIO.app.pac.models.fec_form_3x_submission
const { Election } = DjangIO.app.pac.models.election
const { LedgerSettings } = DjangIO.app.ledger.models
const { PacFileFormStatusType, PacFileFormType } = DjangIO.app.pac.types
const { Organization } = DjangIO.app.userdata.models
const { LedgerReportingJurisdiction } = DjangIO.app.ledger.models

export const getLedgerReportingJurisdictions = () => (dispatch) =>
    dispatch(
        resourceGetDetail(
            LedgerReportingJurisdiction.objects.filter({ jurisdiction_type_ne: 1 }),
            getJurisdictionsActionDict,
        ),
    )

export const getFiling = (id) => (dispatch) =>
    dispatch(resourceGetDetail(PacFile.objects.filter({ download_link: true }), getPacFileActionDict, id))

export const getLatestFiling = (ledgerSettingsId) => {
    try {
        return resourceGetList(PacFile, getLatestPacFileActionDict, {
            latest_non_archived_form3x_pac_file: ledgerSettingsId,
        })
    } catch (error) {
        console.error(error)
        return swal(swalConfigs.UNABLE_TO_GET_START_DATE)
    }
}

export const getGeneralElection = (year) => {
    try {
        return sendAction(Election, getGeneralElectionActionDict, {
            action: "get_general_election",
            kwargs: { year: year },
            method: "get",
        })
    } catch (error) {
        console.error(error)
        return swal(swalConfigs.UNABLE_TO_GET_GENERAL_ELECTION)
    }
}

/**
 * Action creator used to send user to the filings list view
 * @param {Number} ledgerSettingsId - id of legder settings
 */
export const goToFilingsListView = (ledgerSettingsId) => async (dispatch) => {
    const path = getPacPaths({ ledgerSettingsId: ledgerSettingsId })._pacFilingsList
    await dispatch(pushPage(path))
}

/**
 * Action creator used to update the status of a PacFile
 * @param {Number} id - id of PacFile
 */
export const setPacfileStatus = (id, status) =>
    resourcePatchDetail(PacFile.objects, patchPacFileActionDict, id, { status })

/**
 * Action creator used to set the signature timestamp of a PacFile
 * for a Form 1 PacFile once it is ready to be filed.
 * @param {Number} id - id of PacFile
 */
export const setPacfileSignatureTimestamp = (id, timestamp) =>
    resourcePatchDetail(PacFile.objects, patchPacFileActionDict, id, { signature_timestamp: timestamp })

/**
 * Action creator used to generate Dot Fec File for Form3x.
 * @param {Number} id - id of PacFile
 * @returns - updated PacFile after .fec generation
 */
export const generateDotFecFileAndUploadPdfForForm3x = (filingsId, ledgerSettingsId) => async (dispatch) => {
    // If the Async Filings feature flag is enabled, we do NOT want to trigger generation
    // from this method/endpoint; it will be triggered separately elsewhere
    if (isFeatureEnabled("ff_pac_async_filings")) return null
    try {
        await dispatch(
            sendAction(PacFile, generateDotFecFileAndPdfActionDict, {
                action: "generate_dot_fec_file_and_upload_pdf_for_form3x_endpoint",
                kwargs: { pac_file_id: filingsId },
                method: "patch",
                payload: { pac_file_id: filingsId },
            }),
        )
    } catch (error) {
        const text = error.response?.data || "There was an issue preparing your Form 3X"
        BACKENDERROR(error)
        swal({
            title: "Error",
            text,
            icon: "error",
        })

        const path = getPacPaths({ ledgerSettingsId: ledgerSettingsId })._pacFilingsList
        await dispatch(pushPage(path))
    }
}

/**
 * Action creator used to generate Dot Fec File for Form 1.
 * @param {Number} id - id of PacFile
 * @returns - updated PacFile after .fec generation
 */
export const generateDotFecFileAndUploadPdfForForm1 = (id) => async (dispatch, getState) => {
    try {
        const response = await dispatch(
            sendAction(PacFile, generateDotFecFileAndPdfActionDict, {
                action: "generate_dot_fec_file_and_upload_pdf_for_form1_endpoint",
                kwargs: { pac_file_id: id },
                method: "patch",
                payload: { pac_file_id: id },
            }),
        )
        return response
    } catch (error) {
        const text = error.response?.data?.message || "There was an issue preparing your Form 1"
        await swal({
            title: "Error",
            icon: "error",
            text,
        })
        dispatch(
            popPage(
                getPacPaths({
                    ledgerSettingsId: selectSingleLedgerSettings(getState()).get("id"),
                    filingsId: id,
                })._pacFilingsDetail,
            ),
        )
    }
}

export const submitFiling = (id, formType) => async (dispatch) => {
    const confirmFile = await swal(swalConfigs.CONFIRM_FEC_REPORT_FILE)
    if (confirmFile) {
        try {
            swal(swalConfigs.LOADING_FEC_REPORT)
            await dispatch(setPacfileStatus(id, PacFileFormStatusType.queued_for_filing.value))
            if (formType === PacFileFormType.form_f1.value) {
                await dispatch(setPacfileSignatureTimestamp(id, moment(moment.now())))
                await dispatch(generateDotFecFileAndUploadPdfForForm1(id))
            }
            return swal(swalConfigs.SUBMITTING_FEC_REPORT)
        } catch (error) {
            BACKENDERROR(error)
            const details = isValidationError(error) ? decipherValidationErrorMessage(error) : ""
            swal(swalConfigs.submitFecReportError(details))
        }
    }
    return null
}

export const saveHistoricalForm1 = (id) => async (dispatch) => {
    const form = document.createElement("form")
    form.innerHTML = `
            <div>
                <label for="filingDate">Filing Date</label><br/>
                <input type="date" name="submitted_timestamp" />
            </div>
            <br/>
            <div>
                <label for="fecId">FEC ID</label><br/>
                <input type="text" name="fec_id" placeholder="000000000000" />
            </div>
        `
    const formValues = {}
    const inputs = form.getElementsByTagName("input")
    for (let i = 0; i < inputs.length; i++) {
        inputs[i].addEventListener("change", (evt) => {
            formValues[evt.target.name] = evt.target.value
        })
    }

    const result = await swal({
        title: "Save Historical Form 1",
        content: form,
        buttons: ["Cancel", "Save"],
    })
    if (!result) {
        // Cancelled by user
        return
    }

    try {
        swal({
            title: "Saving Historical Form 1...",
            icon: "info",
            buttons: false,
        })
        const timeout = new Promise((resolve) => {
            setTimeout(() => resolve(), 1000)
        })
        await dispatch(
            sendAction(PacFile, submitForm1HistoricalActionDict, {
                actionPk: "submit_historical_form1",
                action: id,
                kwargs: formValues,
                method: "post",
            }),
        )
        await timeout

        swal({
            title: "Success!",
            icon: "success",
        })
    } catch (err) {
        console.error(err)
        swal({
            ...swalConfigs.UNABLE_TO_SAVE_FORM1_HISTORICAL,
            text: err?.response?.data?.message || swalConfigs.UNABLE_TO_SAVE_FORM1_HISTORICAL.text,
        })
    }
}

/**
 * Action creator used to get PDF S3 URL from the PacFile.
 * @param {Number} id - id of PacFile
 * @returns - updated PacFile with valid pdf_s3_url
 */
export const getPdfUrlFromPacFile = (id) =>
    sendAction(PacFile, getPdfUrlActionDict, {
        action: "get_pdf_url_from_pacfile",
        kwargs: { pac_file_id: id },
        method: "get",
        payload: { pac_file_id: id },
    })

/**
 * Action creator used to download PDF Report.
 * Should wait for convertFecToPdfUploadS3 to finish, once finished
 * the returned pacfile should have a valid pdf_s3_url.
 *
 * @param {Number} id - id of PacFile
 * @returns - should download FEC Report PDF
 */
export const downloadPdfReport = (id) => async (dispatch) => {
    swal(swalConfigs.DOWNLOADING_STARTED)
    try {
        const pdfUrl = await dispatch(getPdfUrlFromPacFile(id))
        window.open(pdfUrl, "_BLANK")
        return swal(swalConfigs.DOWNLOADING_SUCCESS)
    } catch (error) {
        BACKENDERROR(error)
        swal(swalConfigs.DOWNLOADING_FAILURE)
    }
    return null
}

/**
 * Action creator used to get .FEC S3 URL from the PacFile.
 * @param {Number} id - id of PacFile
 * @returns - updated PacFile with valid fec_s3_url
 */
export const getDotFecUrlFromPacFile = (id) =>
    sendAction(PacFile, getDotFecUrlActionDict, {
        action: "get_dot_fec_url_from_pacfile",
        kwargs: { pac_file_id: id },
        method: "get",
    })

/**
 * Action creator used to download .FEC Report.
 * @param {Number} id - id of PacFile
 * @returns - should download FECCheck Report MSG
 */
export const downloadDotFecReport = (id) => async (dispatch) => {
    swal(swalConfigs.DOWNLOADING_STARTED)
    try {
        const dotFecUrl = await dispatch(getDotFecUrlFromPacFile(id))
        // There should always exist a .fec file in S3 bucket for this report before triggering this action creator,
        // so an empty url indicates there was an issue generating the .fec file
        if (dotFecUrl === "") {
            return swal(swalConfigs.DOWNLOADING_FAILURE)
        }
        window.open(dotFecUrl, "_BLANK")
        return swal(swalConfigs.DOWNLOADING_SUCCESS)
    } catch (error) {
        BACKENDERROR(error)
        swal(swalConfigs.DOWNLOADING_FAILURE)
    }
    return null
}

/**
 * Action creator used to get FECCheck MSG S3 URL from the PacFile.
 * @param {Number} id - id of PacFile
 * @returns - updated PacFile with valid msg_s3_url
 */
export const getFecCheckMsgUrlFromPacFile = (id) =>
    sendAction(PacFile, getFecCheckMsgUrlActionDict, {
        action: "get_msg_url_from_pacfile",
        kwargs: { pac_file_id: id },
        method: "get",
    })

/**
 * Action creator used to download FECCheck Report.
 * After a pacfile is saved, it should have a valid msg_s3_url if any error or warning is detected by FECCheck.
 * (pacfile.save will trigger _run_feccheck_on_dot_fec_file)
 * @param {Number} id - id of PacFile
 * @returns - should download FECCheck Report MSG
 */
export const downloadFecCheckReport = (id) => async (dispatch) => {
    swal(swalConfigs.DOWNLOADING_STARTED)
    try {
        const msgUrl = await dispatch(getFecCheckMsgUrlFromPacFile(id))
        if (msgUrl === "") {
            return swal(swalConfigs.NO_ERRORS_OR_WARNINGS)
        }
        window.open(msgUrl, "_BLANK")
        return swal(swalConfigs.DOWNLOADING_SUCCESS)
    } catch (error) {
        BACKENDERROR(error)
        swal(swalConfigs.DOWNLOADING_FAILURE)
    }
    return null
}

/**
 * This will be passed to the Redux form's 'validate' function,
 * which is called each time a field input is changed.
 * If the form is rendered, but no value has been selected,
 * return an error object with the field name as its key, and the error message as the value
 * @param {Object} immutableValues - An immutable map
 * @param {Object} props - props that dictate if form field should be rendered
 * @returns {Object}
 */
export const validate = (immutableValues, props) => {
    const errors = {}

    if (!immutableValues.get("form_type")) {
        errors.form_type = "Select a Form Type"
    }
    if (!immutableValues.get("report_type")) {
        errors.form_type = "Select a Report Type"
    }

    return errors
}

/**
 * Action creator responsible for creating the PacFile and FecSingleFormSubmission
 *
 * @param {Object} actionDict - action dict containing information for the PacFile and FormSubmission
 * @param {Object} requestData - fields that specify the transaction (ledger settings, uuid) and provide the event type
 * @returns id of the PacFile and segue to the Report Form
 */
export const createPacFileAndForm = (actionDict, requestData) =>
    sendAction(PacFile, actionDict, {
        action: "create_pacfile_and_form",
        kwargs: requestData,
        method: "post",
        payload: requestData,
    })

/**
 * Action creator responsible for updating a PacFile and FecSingleFormSubmission
 *
 * @param {Object} actionDict - action dict containing information for the PacFile and FormSubmission
 * @param {Object} requestData - fields that specify the transaction (ledger settings, uuid) and provide the event type
 */
export const updatePacFileAndFormOne = (actionDict, requestData) =>
    sendAction(PacFile, actionDict, {
        action: "update_pacfile_and_form_1",
        kwargs: requestData,
        method: "patch",
        payload: requestData,
    })

/**
 * Action creator used to update Form 3X Cash On Hand Beginning Col A or Col B
 */
export const updateForm3XSubmissionCashOnHandBeginning = (values, dispatch, props) => {
    if (values.has("cashOnHandAInput") && values.get("updateFormA")) {
        return dispatch(
            resourcePatchDetail(FecForm3XSubmission, patchForm3XSubmissionActionDict, props.form3xId, {
                col_a_cash_on_hand_beginning: values.get("cashOnHandAInput"),
                ...(props.isJanFirstStartDate && { col_b_cash_on_hand_beginning: values.get("cashOnHandAInput") }),
            }),
        )
    }
    if (values.has("cashOnHandBInput") && values.get("updateFormB")) {
        return dispatch(
            resourcePatchDetail(FecForm3XSubmission, patchForm3XSubmissionActionDict, props.form3xId, {
                col_b_cash_on_hand_beginning: values.get("cashOnHandBInput"),
                ...(props.isJanFirstStartDate && { col_a_cash_on_hand_beginning: values.get("cashOnHandBInput") }),
            }),
        )
    }
    return false
}

/**
 * Action creator used to update Form 3X Election Date
 */
export const updateForm3XSubmissionElectionDate = (values, dispatch, props) => {
    if (values.has("electionDateInput")) {
        return dispatch(
            resourcePatchDetail(FecForm3XSubmission, patchForm3XSubmissionActionDict, props.form3xId, {
                election_date: values.get("electionDateInput"),
            }),
        )
    }
    return false
}

export const updateForm3XSubmissionElectionState = (values, dispatch, props) => {
    if (values.has("stateInput")) {
        const response = dispatch(
            resourcePatchDetail(FecForm3XSubmission, patchForm3XSubmissionActionDict, props.form3xId, {
                election_region: values.get("stateInput"),
            }),
        )
        return response
    }
    return false
}

export const updateForm3XSubmissionElectionType = (values, dispatch, props) => {
    if (values.has("typeInput")) {
        return dispatch(
            resourcePatchDetail(FecForm3XSubmission, patchForm3XSubmissionActionDict, props.form3xId, {
                election_type: values.get("typeInput"),
            }),
        )
    }
    return false
}

/**
 * This will be passed to the Redux form's 'onSubmit' function,
 * which is called when the user submits a valid form.
 * @param {Object} immutableFormValues - An immutable map of the Redux form values
 * @param {Function} dispatch - function to dispatch actions, not used in this action creator
 * @param {Object} props - component props
 * @returns {undefined | Promise}
 */
export const onSubmit = (immutableFormValues, dispatch, props) => {
    const coverageStartDate = props.specialFilingCoverageStart
    const coverageEndDate = moment(props.specialFilingCoverageEnd, "YYYY-MM-DD").format("MM/DD/YYYY")
    const requestData = {
        ledger_settings: LedgerSettings.resourceUriFromId(props.ledgerSettingsId),
        amendment_number: 0,
        form_type: immutableFormValues.get("form_type"),
        form_period_type: props.filingFrequency,
        organization: Organization.resourceUriFromId(Userdata.organization_id),
        region: DjangIO.app.models.SupportedRegion.federal.value, // TODO: all filings will be federal initially
        status: PacFileFormStatusType.in_progress.value,
        coverage_start_date: coverageStartDate,
        coverage_end_date: coverageEndDate,
        report_code: immutableFormValues.get("report_type"),
    }
    return dispatch(createPacFileAndForm(postPacFileActionDict, requestData))
}

/**
 * This will be passed to the Redux form's 'onSubmitSuccess' function,
 * which is called when the form is valid and 'onSubmit' was successful.
 * Pop to previous page (or registration page list view if no previous page)
 * And destroy form
 * @param {Object} result - The value from 'onSubmit'
 * @param {Function} dispatch - function to dispatch actions
 * @param {Object} props - component props
 * @returns {undefined}
 */
export const onSubmitSuccess = (result, dispatch, props) => {
    // Redirect to filing detail page
    props.pushPage(
        getPacPaths({
            ledgerSettingsId: props.ledgerSettingsId,
            filingsId: result.id,
        })._pacFilingsDetail,
    )
}

/**
 * Display a swal message that contains the button "View Filing",
 * If the user clicks "View Filing", segue to the conflicting PacFile's FilingsDetailView
 * @param {Number} filingsId - id of conflicting pacfile
 * @param {String} ledgerSettingsId - user's ledger settings id
 * @param {String} message - error message
 * @param {Object} props - component props
 */
export const segueToConflictingPacFile = (filingsId, ledgerSettingsId, message, props) => async () => {
    const isConfirm = await swal({
        icon: "error",
        title: "Error",
        text: message,
        buttons: ["Cancel", "View Filing"],
    })
    if (isConfirm) {
        props.pushPage(getPacPaths({ ledgerSettingsId, filingsId })._pacFilingsDetail)
    }
    return null
}

/**
 * This will be passed to the Redux form's 'onSubmitFail' function,
 * which is called if the form is invalid or 'onSubmit' was unsuccessful.
 * If the error object returned a message and field_errors, display the custom
 * error message and link to the conflicting pacfile, (handled in segueToConflictingPacFile)
 * Else, swal a generic error message
 * @param {Object} result - Value from 'onSubmit'
 * @param {Function} dispatch - function to dispatch actions
 * @param {Object?} error - error object(?)
 * @param {Object} props - component props
 * @returns {undefined}
 */
export const onSubmitFail = (result, dispatch, error, props) => {
    const errorData = error.response && error.response.data
    let errorText = "There was a problem saving the Filing. Please contact us at support@quorum.us"
    if (props.invalid) {
        errorText = Object.values(props.syncErrors).join("\n")
    }

    if (errorData) {
        const { message, fieldErrors } = errorData
        const filingsId = fieldErrors && fieldErrors.pacfile
        const { ledgerSettingsId } = props.params
        errorText = message
        if (filingsId) {
            return dispatch(segueToConflictingPacFile(filingsId, ledgerSettingsId, message, props))
        }
    }

    swal({
        icon: "error",
        title: "Error",
        text: errorText,
    })

    return errorText
}

/**
 * Dispatch to the filing submit page from the filing page, by grabbing the filing info from the store.
 */
export const segueToFilingSubmit = () => async (dispatch, getState) => {
    const filing = getState().pac.get("filing")
    const filingsId = filing.get("id")
    const ledgerSettingsId = LedgerSettings.idFromResourceUri(filing.get("ledger_settings"))
    const formType = filing.get("form_type")

    const path = getPacPaths({
        filingsId,
        ledgerSettingsId,
        formType,
        jurisdictionKey: "federal",
    })

    await dispatch(
        pushPage(isFeatureEnabled("ff_pac_async_filings") ? path._pacFilingsReviewNew : path._pacFilingsReview),
    )
}

/**
 * Handle Segueing to the Look and File page. Even if there's a delay, won't resolve until segue is complete.
 *
 * Usually this should just call segueToFilingSubmit, but we need to handle a race condition where if the user changes
 * the COH we need to wait for that request to be successful calling that action creator.
 */
export const handleLookAndFile = () => async (dispatch, getState) => {
    if (selectIsFilingFormSubmitting(getState())) {
        const swalClosePromise = swal(swalConfigs.FILING_STILL_SUBMITTING_LOADING)
        await new Promise((resolve) => {
            const unsubscribe = window.store.subscribe(() => {
                if (!selectIsFilingFormSubmitting(getState())) {
                    unsubscribe()
                    swal.close()
                    resolve()
                }
            })
        })
        await swalClosePromise
    }

    await dispatch(segueToFilingSubmit())
}

/**
 * Create a special form3x report (e.g. pre-election) through using the content of filing form and searchify
 * @returns {Undefined or Promise}
 */
export const createSpecialForm3X =
    ({ searchifyFilters }) =>
    async (dispatch, getState) => {
        const currentFlvId = getState().search.get("currentFlvId")
        const {
            ledgerSettingsId,
            formType,
            reportType,
            specialFilingCoverageStart,
            specialFilingCoverageEnd,
            filingFrequency,
        } = getState().search.getIn(["filterableListViews", currentFlvId, "searchify", "searchifySubmitParams"]).toJS()
        const electionId = searchifyFilters["addedIds"][0]
        const requestData = {
            ledger_settings: LedgerSettings.resourceUriFromId(ledgerSettingsId),
            form_type: formType,
            form_period_type: filingFrequency,
            organization: Organization.resourceUriFromId(Userdata.organization_id),
            region: DjangIO.app.models.SupportedRegion.federal.value, // TODO: all filings will be federal initially
            status: PacFileFormStatusType.in_progress.value,
            coverage_start_date: moment(specialFilingCoverageStart).format("MM/DD/YYYY"),
            coverage_end_date: moment(specialFilingCoverageEnd).format("MM/DD/YYYY"),
            report_code: reportType,
            election: Election.resourceUriFromId(electionId),
        }

        await dispatch(createPacFileAndForm(postPacFileActionDict, requestData))

        const filingsId = getState().pac.getIn(["filing", "id"])
        const path = getPacPaths({ filingsId, ledgerSettingsId })._pacFilingsDetail
        dispatch(pushPage(path))
    }
